import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * iOS-style toggle switch for Loop functionality.
 *
 * @fires loop-change - Fired when the toggle state changes (detail: { enabled: boolean })
 */
@customElement('loop-toggle')
export class LoopToggle extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
                width: 70px;
                height: 41px;
                cursor: pointer;
            }

            :host([disabled]) {
                opacity: 0.5;
                cursor: not-allowed;
                pointer-events: none;
            }

            .switch {
                position: relative;
                width: 100%;
                height: 100%;
                background-color: #E5E5EA;
                border-radius: 41px;
                transition: background-color 0.3s ease;
            }

            :host([enabled]) .switch {
                background-color: #007AFF;
            }

            .switch:hover {
                background-color: #D1D1D6;
            }

            :host([enabled]) .switch:hover {
                background-color: #0066DD;
            }

            .knob {
                position: absolute;
                height: 37px;
                width: 37px;
                left: 2px;
                bottom: 2px;
                background-color: white;
                border-radius: 50%;
                transition: transform 0.3s ease;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            }

            :host([enabled]) .knob {
                transform: translateX(39px);
            }

            .label {
                position: absolute;
                top: 50%;
                left: 34px;
                transform: translateY(-50%);
                font-size: 11px;
                font-weight: 600;
                color: #8E8E93;
                transition: left 0.3s ease, color 0.3s ease;
                user-select: none;
            }

            :host([enabled]) .label {
                left: 10px;
                color: white;
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    enabled = false;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    private _onClick(): void {
        if (this.disabled) return;
        this.enabled = !this.enabled;
        this.dispatchEvent(
            new CustomEvent('loop-change', {
                bubbles: true,
                composed: true,
                detail: { enabled: this.enabled },
            })
        );
    }

    render() {
        return html`
            <div
                class="switch"
                role="switch"
                aria-checked=${this.enabled}
                aria-label="Loop"
                tabindex="0"
                @click=${this._onClick}
                @keydown=${(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this._onClick();
                    }
                }}
            >
                <span class="knob"></span>
                <span class="label">Loop</span>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'loop-toggle': LoopToggle;
    }
}
